import { Injectable, inject } from '@angular/core';
import { Observable, map, catchError, of, forkJoin } from 'rxjs';
import {
  SystemBackgroundTaskService,
  SystemBackgroundTaskResultService,
  SystemBackgroundTask,
  SystemBackgroundTaskResult,
  PosibleExecuteType,
  SystemBackgroundTaskType,
  SystemBackgroundTaskResultType,
  SortType
} from '@6grain/masofa-monolith-api-client/common';
import {
  TaskDisplayItem,
  TaskResultDisplayItem
} from '@models/task-scheduler.models';
import { FieldFilter } from '@6grain/masofa-monolith-api-client/dictionaries';

@Injectable({
  providedIn: 'root'
})
export class TaskSchedulerService {
  private systemBackgroundTaskService = inject(SystemBackgroundTaskService);
  private systemBackgroundTaskResultService = inject(SystemBackgroundTaskResultService);

  /**
   * Получает список всех фоновых задач через GetPosibleExecuteTypes
   */
  getSystemBackgroundTasks(): Observable<PosibleExecuteType[]> {
    return this.systemBackgroundTaskService.commonSystemBackgroundTaskGetPosibleExecuteTypesGet().pipe(
      catchError(error => {
        console.error('Error fetching system background tasks:', error);
        return of([]);
      })
    );
  }

  /**
   * Получает результаты выполнения задач
   */
  getSystemBackgroundTaskResults(): Observable<SystemBackgroundTaskResult[]> {
    return this.systemBackgroundTaskResultService.commonSystemBackgroundTaskResultGetByQueryPost({
      take: 50,
      offset: 0,
      sortBy: 'createAt',
      sort: SortType.NUMBER_1,
    }).pipe(
      catchError(error => {
        console.error('Error fetching system background task results:', error);
        return of([]);
      })
    );
  }

  /**
   * Получает реальные задачи из базы данных
   */
  getRealSystemBackgroundTasks(): Observable<SystemBackgroundTask[]> {
    return this.systemBackgroundTaskService.commonSystemBackgroundTaskGetByQueryPost({}).pipe(
      catchError(error => {
        console.error('Error fetching real system background tasks:', error);
        return of([]);
      })
    );
  }

  /**
   * Получает возможные типы выполнения задач
   */
  getPosibleExecuteTypes(): Observable<PosibleExecuteType[]> {
    return this.systemBackgroundTaskService.commonSystemBackgroundTaskGetPosibleExecuteTypesGet().pipe(
      catchError(error => {
        console.error('Error fetching possible execute types:', error);
        return of([]);
      })
    );
  }

   /**
    * Gets tasks for display in UI
    * Now supports optional filters (FieldFilter[]) for requesting real SystemBackgroundTask from DB
    */
   getTasksForDisplay(filters?: FieldFilter[]): Observable<TaskDisplayItem[]> {
     // If filters are provided - get real tasks and transform them
     if (filters && filters.length > 0) {
       return this.systemBackgroundTaskService.commonSystemBackgroundTaskGetByQueryPost({ filters }).pipe(
         map((tasks: SystemBackgroundTask[]) => tasks.map(task => this.transformSystemBackgroundTaskToDisplayItem(task))),
         catchError(error => {
           console.error('Error fetching system background tasks by filters:', error);
           return of([]);
         })
       );
     }

     // By default return possible execution types as before
    return this.getSystemBackgroundTasks().pipe(
      map(possibleExecuteTypes => possibleExecuteTypes.map(possibleType => this.transformPossibleExecuteTypeToDisplayItem(possibleType)))
    );
  }

  // /**
  //  * Получает результаты выполнения задач в виде, готовом к отображению
  //  */
  // getTaskResultsForDisplay(): Observable<TaskResultDisplayItem[]> {
  //   return this.systemBackgroundTaskResultService.commonSystemBackgroundTaskResultGetByQueryPost({}).pipe(
  //     map((results: SystemBackgroundTaskResult[]) => results.map(r => this.transformResultToDisplayItem(r))),
  //     catchError(error => {
  //       console.error('Error fetching task results for display:', error);
  //       return of([]);
  //     })
  //   );
  // }

  /**
   * Трансформирует сущность SystemBackgroundTask (из БД) в элемент для отображения
   */
  private transformSystemBackgroundTaskToDisplayItem(task: SystemBackgroundTask): TaskDisplayItem {
    const className = task.executeTypeName?.split('.').pop() || 'Unknown';

    return {
      id: task.id || '',
      names: (task.names as any)?.['ru'] || className,
      executeTypeName: className,
      taskType: task.taskType || 0,
      isActive: typeof task.isActive === 'boolean' ? task.isActive : true,
      executionCount: task.executionCount || 0,
      maxExecutions: typeof task.maxExecutions === 'number' ? task.maxExecutions : -1,
      lastRun: 'Never',
      status: task.isActive ? 'active' : 'inactive',
      icon: this.getTaskIconByType(task.taskType || 0),
      iconClass: this.getTaskIconClassByType(task.taskType || 0)
    };
  }

  /**
   * Трансформирует результат в элемент для отображения
   */
  // private transformResultToDisplayItem(result: SystemBackgroundTaskResult): TaskResultDisplayItem {
  //   return {
  //     id: result.id || '',
  //     taskName: 'Task', // TODO: Получить имя задачи по ID
  //     executeTypeName: 'Unknown', // TODO: Получить имя типа выполнения
  //     resultType: result.resultType || 1, // SystemBackgroundTaskResultType.Failed
  //     createdAt: result.createAt || result.lastUpdateAt || 'Unknown',
  //     status: result.resultType === 0 ? 'success' : 'failed', // 0 = Success, 1 = Failed
  //     duration: undefined // TODO: Вычислить длительность выполнения
  //   };
  // }

  /**
   * Трансформирует результат в элемент для отображения с информацией о задаче
   */
  private transformResultToDisplayItemWithTask(result: SystemBackgroundTaskResult, task?: SystemBackgroundTask): TaskResultDisplayItem {
    const taskName = task ? ((task.names as any)?.['ru'] || task.executeTypeName?.split('.').pop() || 'Unknown Task') : 'Unknown Task';
    const executeTypeName = task ? (task.executeTypeName?.split('.').pop() || 'Unknown') : 'Unknown';

    return {
      id: result.id || '',
      taskName: taskName,
      executeTypeName: executeTypeName,
      resultType: result.resultType || 1, // SystemBackgroundTaskResultType.Failed
      createdAt: result.createAt || result.lastUpdateAt || 'Unknown',
      status: result.resultType === 0 ? 'success' : 'failed', // 0 = Success, 1 = Failed
      duration: undefined // TODO: Вычислить длительность выполнения
    };
  }


  /**
   * Получает иконку для задачи по типу
   */
  private getTaskIconByType(taskType: number): string {
    switch (taskType) {
      case 0: // SystemBackgroundTaskType.Schedule
        return '../../../assets/icons/main/light/calendar-2-fill.svg';
      case 1: // SystemBackgroundTaskType.Command
        return '../../../assets/icons/main/light/terminal-box-fill.svg';
      case 2: // SystemBackgroundTaskType.Condition
        return '../../../assets/icons/main/light/flow-chart.svg';
      default:
        return '../../../assets/icons/main/light/task-fill.svg';
    }
  }

  /**
   * Получает CSS класс для иконки задачи по типу
   */
  private getTaskIconClassByType(taskType: number): string {
    switch (taskType) {
      case 0: // SystemBackgroundTaskType.Schedule
        return 'schedule-icon';
      case 1: // SystemBackgroundTaskType.Command
        return 'command-icon';
      case 2: // SystemBackgroundTaskType.Condition
        return 'condition-icon';
      default:
        return 'default-icon';
    }
  }

  /**
   * Получает название типа задачи
   */
  getTaskTypeName(taskType?: number): string {
    switch (taskType) {
      case 0: // SystemBackgroundTaskType.Schedule
        return 'Schedule';
      case 1: // SystemBackgroundTaskType.Command
        return 'Command';
      case 2: // SystemBackgroundTaskType.Condition
        return 'Condition';
      default:
        return 'Unknown';
    }
  }

  /**
   * Получает название статуса результата
   */
  getResultStatusName(resultType: number): string {
    switch (resultType) {
      case 0: // SystemBackgroundTaskResultType.Success
        return 'Success';
      case 1: // SystemBackgroundTaskResultType.Failed
        return 'Failed';
      default:
        return 'Unknown';
    }
  }

  /**
   * Трансформирует PosibleExecuteType (возможный тип выполнения) в элемент для отображения
   */
  private transformPossibleExecuteTypeToDisplayItem(possible: PosibleExecuteType): TaskDisplayItem {
    // PosibleExecuteType содержит поля: executeTypeName, names, taskType, groupName, parametrsJsonSchema
    const executeTypeName = possible?.executeTypeName || 'Unknown';
    const className = executeTypeName.split('.').pop() || executeTypeName;

    // taskType в модели -- enum; приводим к number для представления
    const taskTypeNumber = typeof possible?.taskType === 'number' ? (possible.taskType as unknown as number) : 0;

    return {
      id: executeTypeName,
      names: (possible?.names as any)?.['ru'] || className,
      executeTypeName: className,
      taskType: taskTypeNumber,
      isActive: true,
      executionCount: 0,
      maxExecutions: -1,
      lastRun: 'Never',
      status: 'active',
      icon: this.getTaskIconByType(taskTypeNumber),
      iconClass: this.getTaskIconClassByType(taskTypeNumber)
    };
  }
}
